import { useEffect, useMemo } from "react";

import { ApiError } from "@/api/client";
import { HealthRing } from "@/components/HealthRing";
import { type AlarmLevel, KpiCard } from "@/components/KpiCard";
import { healthScore } from "@/features/kpi/health";
import { t } from "@/i18n";

import { useEmbedCardsQuery, useEmbedKpiSnapshotQuery } from "./api";

const ALARM_LEVEL_MAP: Record<string, AlarmLevel> = { critical: "critical", warning: "warning", ok: "normal" };

function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/**
 * Hafif ozet govdesi — saglik halkasi + ilk 4 "ozet" sekmesi karti (sozlesme
 * §2: "sağlık + 4 kart"). `not_supported` kartlar Ozet.tsx ile AYNI kuralla
 * (kart listesinden tamamen) elenir; gomulu modda derinlemesine
 * inceleme/analiz YOK (yalniz tam ekrana yonlendirme) — bu yuzden KpiCard
 * `onOpenDetail`/`onAnalyze` verilmez.
 *
 * `onUnauthorized`: `useEmbedSession` onbellekteki (sessionStorage) bir
 * bearer bulup POST ATMADAN "ready" doner (bkz. o dosyanin dokumantasyonu) —
 * o jetonun 2 saatlik omru bu ARADA dolmus olabilir. Bu durumda ilk veri
 * cagrisi (401) burada YAKALANIR, onbellek temizlenir ve sayfa
 * `useEmbedSession().reportInvalid()` araciligiyla "tam ekranda giris yap"
 * durumuna geri doner — aksi halde kullanici sonsuza kadar bos bir izgara
 * gorurdu (yeniden POST atma girisimi YOK: 2 saatlik omur dolunca oturum
 * sessizce yenilenmez, kullanici tam ekranda tekrar girer).
 */
export function EmbedSummary({ enabled, onUnauthorized }: { enabled: boolean; onUnauthorized: () => void }) {
  const cardsQuery = useEmbedCardsQuery(enabled);
  const snapshotQuery = useEmbedKpiSnapshotQuery(enabled);

  const cardsUnauthorized = isUnauthorized(cardsQuery.error);
  const snapshotUnauthorized = isUnauthorized(snapshotQuery.error);

  useEffect(() => {
    if (cardsUnauthorized || snapshotUnauthorized) onUnauthorized();
  }, [cardsUnauthorized, snapshotUnauthorized, onUnauthorized]);

  const kpis = snapshotQuery.data?.kpis ?? null;
  const health = useMemo(
    () => (kpis ? healthScore(kpis) : (snapshotQuery.data?.health_score ?? null)),
    [kpis, snapshotQuery.data],
  );

  const cards = (cardsQuery.data?.cards ?? [])
    .filter((card) => card.tab === "ozet" && card.state !== "not_supported")
    .slice(0, 4);

  const loading = enabled && (cardsQuery.isLoading || snapshotQuery.isLoading);
  // 401 disi bir hata (ör. 5xx) — kart/anlik goruntu sorgulari basarisiz
  // olmus, bos bir izgarayi "veri yok" gibi gostermek yerine acikca belirtilir.
  const hasOtherError =
    (cardsQuery.isError && !cardsUnauthorized) || (snapshotQuery.isError && !snapshotUnauthorized);

  if (cardsUnauthorized || snapshotUnauthorized) return null;

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <HealthRing value={health} size={72} />

      {loading && <p className="text-sm text-muted">{t("common.loading")}</p>}

      {!loading && hasOtherError && <p className="text-sm text-red">{t("embed.error")}</p>}

      {!loading && !hasOtherError && (
        <div className="grid w-full grid-cols-2 gap-3" data-testid="embed-kpi-grid">
          {cards.map((card) => {
            const entry = kpis?.[card.id];
            const count = entry && typeof entry === "object" ? (entry as { count?: number }).count : undefined;
            const alarm =
              entry && typeof entry === "object" ? (entry as { alarm_level?: string }).alarm_level : undefined;
            return (
              <KpiCard
                key={card.id}
                title={card.title}
                state={card.state}
                reason={card.reason}
                value={card.state === "live" ? (count ?? null) : null}
                alarmLevel={alarm ? (ALARM_LEVEL_MAP[alarm] ?? "normal") : "normal"}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
